import angular from 'angular'

import { over, flow, wrap, assign, padEnd, forEach } from 'lodash-es'
import { isZero, callWith, defaultTo, numberWithSign } from '../utils/bits'

import Ticker from '../utils/ticker'
import deline from '../utils/deline'
import envRaw from '../utils/env-raw'
import MyImageLoaded from '../utils/my-image-loaded'

angular
  .module('ggs.img-queue')
  .factory('imgQueueInternal', ImgQueueInternalProvider)
  .factory('imgQueue', ImgQueueProvider)

const TIMEOUT = { isTimeout: true }
const TICKER_DELAY = 1e3
const TIMEOUT_DELAY = 10e3
const PROCESSING_LIMIT = 6

function ImgQueueInternalProvider(qh, $log, $rootScope) {

  let tasks = 0

  const regs = []
  const queue = []
  const ticker = Ticker(TICKER_DELAY)

  $rootScope.$on('$destroy', ticker.clear)

  function stayInQueue(reg) {
    const duplicate = findReg(reg.src)
    if (duplicate) {
      useDuplicate(reg, duplicate)
    } else {
      regs.push(reg)
      queue.push(reg)
      printDebug('stayInQueue')
      tryQueue()
    }
  }

  function runImmediately(reg) {
    const duplicate = findReg(reg.src)
    if (duplicate) {
      useDuplicate(reg, duplicate)
    } else {
      regs.push(reg)
      tasks++
      printDebug('runImmediately')
      runReg(reg)
    }
  }

  function taskCallback() {
    if (tasks > 0) {
      tasks--
      printDebug('taskCallback')
    }

    tryQueue()
  }

  function tryQueue() {
    const canRun = PROCESSING_LIMIT - tasks
    if (canRun <= 0) return
    const local = queue.splice(0, canRun)
    tasks += local.length
    printDebug('tryQueue')
    for (const reg of local) {
      runReg(reg)
    }
  }

  function useDuplicate(reg, duplicate) {
    if (duplicate.isProcessing) {
      if (!duplicate.events.done.payload) {
        const payload = []
        duplicate.events.done = over(duplicate.events.done, flow(callWith, wrap(payload, forEach)))
        duplicate.events.done.payload = payload
      }

      if (!duplicate.events.fail.payload) {
        const payload = []
        duplicate.events.fail = over(duplicate.events.fail, flow(callWith, wrap(payload, forEach)))
        duplicate.events.fail.payload = payload
      }

      duplicate.events.done.payload.push(reg.events.done)
      duplicate.events.fail.payload.push(reg.events.fail)
    } else {
      duplicate.size
        ? reg.events.done(duplicate.size)
        : reg.events.fail(duplicate.err)
    }
  }

  function pushReg(reg) {
    regs.push(reg)
  }

  function findReg(src) {
    for (const reg of regs) {
      if (reg.isKilled) {
        continue
      }

      if (reg.src === src) {
        return reg
      }
    }

    return null
  }

  function runReg(reg) {
    const tick = Math.ceil(TIMEOUT_DELAY / TICKER_DELAY)
    ticker.oneTick(tick, onTimeout, reg)
    reg.isProcessing = true
    reg.myImageLoader = new MyImageLoaded(reg.src, reg.target, onProcessed, reg)
    reg.myImageLoader.init()
  }

  function onTimeout() {
    const reg = this
    const err = assign(new Error(`imgQueue: [timeout=${TIMEOUT_DELAY}] for [src=${reg.src}] exceeded!`), TIMEOUT)
    return onProcessed.call(reg, err)
  }

  function onProcessed(err = null, size = null) {
    const reg = this

    if (reg.isKilled) return
    if (reg.isProcessed) return

    ticker.off(onTimeout, reg)

    reg.err = err
    reg.size = size
    reg.isKilled = false
    reg.isProcessed = true
    reg.isProcessing = false

    reg.size
      ? reg.myImageLoader.cleanup()
      : reg.myImageLoader.kill()
    reg.myImageLoader = null

    reg.size
      ? reg.events.done(reg.size)
      : reg.events.fail(reg.err)
    reg.events = null

    taskCallback()
  }

  function killReg(reg) {
    if (reg.isKilled) return
    if (reg.isProcessed) return

    ticker.off(onTimeout, reg)

    reg.err = qh.markCanceled(new Error(`imgQueue: [src=${reg.src}] canceled!`))
    reg.size = null
    reg.isKilled = true
    reg.isProcessed = false
    reg.isProcessing = false

    const rIndex = regs.indexOf(reg)
    if (rIndex !== -1) {
      regs.splice(rIndex, 1)
      printDebug('killReg')
    }

    const qIndex = queue.indexOf(reg)
    if (qIndex !== -1) {
      queue.splice(qIndex, 1)
      printDebug('killReg')
    }

    if (reg.myImageLoader) {
      reg.myImageLoader.kill()
      reg.myImageLoader = null
    }

    if (reg.events) {
      reg.events.fail(reg.err)
      reg.events = null
    }

    if (rIndex !== -1) {
      taskCallback()
    }
  }

  function getTasksCount() {
    return tasks
  }

  function removeReg(reg) {
    const index = regs.indexOf(reg)
    if (index !== -1) {
      regs.splice(index, 1)
      printDebug('removeReg')
      return true
    } else {
      return false
    }
  }

  function printDebug(cause) {
    if (envRaw.showsImgQueueDebug) {
      const diffTasks = tasks - defaultTo(printDebug.__lastTasks__, 0)
      const diffRegs = regs.length - defaultTo(printDebug.__lastRegs__, 0)
      const diffQueue = queue.length - defaultTo(printDebug.__lastQueue__, 0)
      if (diffTasks === 0 && diffRegs === 0 && diffQueue === 0) {
        return
      }

      $log.force.debug(deline(`
        imgQueue ${padEnd(cause, 15, '_')}
        [${diffTasks === 0 ? '-' : 'T'}]
        [${diffRegs === 0 ? '-' : 'R'}]
        [${diffQueue === 0 ? '-' : 'Q'}]
        { tasks: ${tasks}(${numberWithSign(diffTasks)}),
          regs: ${regs.length}(${numberWithSign(diffRegs)}),
          queue: ${queue.length}(${numberWithSign(diffQueue)}) }`))

      printDebug.__lastTasks__ = tasks
      printDebug.__lastRegs__ = regs.length
      printDebug.__lastQueue__ = queue.length
    }
  }

  return {
    stayInQueue,
    runImmediately,
    pushReg,
    findReg,
    killReg,
    removeReg,
    getTasksCount
  }
}

function ImgQueueProvider(
  $q,
  waitFor,
  $rootScope,
  imgQueueInternal) {

  function makeReg(src, runImmediately) {
    return $q((done, fail) => {
      const reg = {
        src,
        err: null,
        size: null,
        target: null,
        events: { done, fail },
        isKilled: false,
        isProcessed: false,
        isProcessing: false,
        myImageLoader: null
      }

      $rootScope.$on('$destroy', onDestroy)

      !runImmediately
        ? imgQueueInternal.stayInQueue(reg)
        : imgQueueInternal.runImmediately(reg)

      function onDestroy() {
        imgQueueInternal.killReg(reg)
      }
    })
  }

  function stayInQueue(src) {
    return makeReg(src, false)
  }

  function runImmediately(src) {
    return makeReg(src, true)
  }

  function cancel(src) {
    const reg = imgQueueInternal.findReg(src)
    if (reg) {
      imgQueueInternal.killReg(reg)
    }
  }

  function waitOnIdle() {
    return waitFor(imgQueueInternal.getTasksCount, isZero)
  }

  function lookup(src) {
    const reg = imgQueueInternal.findReg(src)
    return reg && reg.size
      ? reg.size
      : null
  }

  function forget(src) {
    const reg = imgQueueInternal.findReg(src)
    if (reg.isProcessed) {
      imgQueueInternal.removeReg(reg)
      return true
    } else {
      return false
    }
  }

  return {
    cancel,
    lookup,
    forget,
    waitOnIdle,
    stayInQueue,
    runImmediately
  }
}
